import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';

import { STORAGE_KEYS } from '../common/constants';

type Theme = 'light' | 'dark';

interface UIState {
    theme: Theme;
    sidebarOpen: boolean;
    commandPaletteOpen: boolean;

    // Actions
    toggleTheme: () => void;
    setTheme: (theme: Theme) => void;
    toggleSidebar: () => void;
    setSidebarOpen: (open: boolean) => void;
    setCommandPaletteOpen: (open: boolean) => void;
    initTheme: () => void;
}

export const useUIStore = create<UIState>()(
    persist(
        (set, get) => ({
            theme: 'light',
            sidebarOpen: true,
            commandPaletteOpen: false,

            toggleTheme: () => {
                const newTheme = get().theme === 'light' ? 'dark' : 'light';
                set({ theme: newTheme });

                // Update document class
                if (newTheme === 'dark') {
                    document.body.classList.add('mode-dark');
                } else {
                    document.body.classList.remove('mode-dark');
                }
            },

            setTheme: (theme) => {
                set({ theme });

                // Update document class
                if (theme === 'dark') {
                    document.body.classList.add('mode-dark');
                } else {
                    document.body.classList.remove('mode-dark');
                }
            },

            toggleSidebar: () => {
                set((state) => ({ sidebarOpen: !state.sidebarOpen }));
            },

            setSidebarOpen: (open) => {
                set({ sidebarOpen: open });
            },

            setCommandPaletteOpen: (open) => {
                set({ commandPaletteOpen: open });
            },

            initTheme: () => {
                const { theme } = get();

                // Check for browser preference if no stored theme
                if (!sessionStorage.getItem(STORAGE_KEYS.THEME)) {
                    const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
                    const initialTheme = prefersDark ? 'dark' : 'light';
                    set({ theme: initialTheme });

                    if (initialTheme === 'dark') {
                        document.body.classList.add('mode-dark');
                    }
                } else {
                    // Apply stored theme
                    if (theme === 'dark') {
                        document.body.classList.add('mode-dark');
                    }
                }
            },
        }),
        {
            name: STORAGE_KEYS.THEME,
            storage: createJSONStorage(() => sessionStorage),
            partialize: (state) => ({ theme: state.theme, sidebarOpen: state.sidebarOpen }),
        },
    ),
);
